import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  CREATE_CMZ_LIBRARY_QUOTE,
  UPDATE_CMZ_LIBRARY_QUOTE,
} from "../../../graphql/IslamicQuotes/mutation";
import { GET_CMZ_LIBRARY_QUOTES } from "../../../graphql/IslamicQuotes/query";
import { IslamicQuote } from "../../../graphql/IslamicQuotes/types";
import {
  DEFAULT_TRANSLATION_ID,
  parseVerseKey,
  quranContentUrl,
  stripHtml,
  useVerseText,
} from "./quran";

/** Cap for loading existing QURAN library pointers (duplicate detection). */
const LIBRARY_VERSE_LOOKUP_LIMIT = 2000;

const verseKeyOf = (surah: number, ayah: number) => `${surah}:${ayah}`;

type ChapterMeta = { id: number; name_simple: string; name_arabic: string };

type QuranSearchResult = {
  verse_key: string;
  text: string;
  translations: { resource_name: string; text: string }[];
};

type Props = {
  open: boolean;
  quote: IslamicQuote | null; // null → create mode
  onClose: (didSave: boolean) => void;
};

type FormState = {
  reference: string;
  surahNumber: number | null;
  ayahNumber: number | null;
  // Preview text captured at pick time (for instant display); edit mode fetches
  //   it via the cmz-backend Quran proxy instead. Never persisted — the backend
  //   stores only the verse pointer.
  previewArabic: string;
  previewTranslation: string;
  tags: string[];
  labels: string[];
  isVerified: boolean;
  isActive: boolean;
};

const emptyForm: FormState = {
  reference: "",
  surahNumber: null,
  ayahNumber: null,
  previewArabic: "",
  previewTranslation: "",
  tags: [],
  labels: [],
  isVerified: true,
  isActive: true,
};

// ─── Reusable free-solo chip input ────────────────────────────────────────────
const ChipInput: React.FC<{
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => (
  <Autocomplete
    multiple
    freeSolo
    size="small"
    options={[]}
    value={value}
    onChange={(_, v) => {
      const cleaned = (v as string[]).map((t) => t.trim()).filter(Boolean);
      onChange(Array.from(new Set(cleaned)));
    }}
    renderTags={(val, getTagProps) =>
      val.map((option, index) => (
        <Chip
          size="small"
          variant="outlined"
          label={option}
          {...getTagProps({ index })}
          key={option}
          sx={{ fontSize: 11 }}
        />
      ))
    }
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        placeholder={value.length === 0 ? placeholder : "Type and press Enter"}
      />
    )}
  />
);

const QuoteFormDialog: React.FC<Props> = ({ open, quote, onClose }) => {
  const isEdit = !!quote;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Whether the verse-search UI is shown. Create starts in search; once a verse
  //   is picked we collapse to the selected-verse view (with a "Change" action).
  const [searchMode, setSearchMode] = useState(true);

  // Quran search state
  const [chapters, setChapters] = useState<Record<number, ChapterMeta>>({});
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<QuranSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchSeq = useRef(0);

  const hasVerse = !!form.surahNumber && !!form.ayahNumber;

  // Existing CMZ library QURAN pointers — used to mark search hits as already
  // saved and to block creating the same surah:ayah twice.
  const { data: libraryData, refetch: refetchLibraryLookup } = useQuery(
    GET_CMZ_LIBRARY_QUOTES,
    {
      variables: {
        filter: {
          category: "QURAN",
          page: 1,
          limit: LIBRARY_VERSE_LOOKUP_LIMIT,
        },
      },
      skip: !open,
      fetchPolicy: "cache-and-network",
    }
  );

  const savedByVerseKey = useMemo(() => {
    const map = new Map<string, IslamicQuote>();
    for (const q of libraryData?.getCMZLibraryQuotes?.quotes || []) {
      if (q.surahNumber != null && q.ayahNumber != null) {
        map.set(verseKeyOf(q.surahNumber, q.ayahNumber), q);
      }
    }
    return map;
  }, [libraryData]);

  const selectedVerseKey =
    form.surahNumber != null && form.ayahNumber != null
      ? verseKeyOf(form.surahNumber, form.ayahNumber)
      : null;

  /** Another library row already owns this verse (ignore the quote we're editing). */
  const duplicateOf = (key: string): IslamicQuote | undefined => {
    const existing = savedByVerseKey.get(key);
    if (!existing) return undefined;
    if (quote && existing.id === quote.id) return undefined;
    return existing;
  };

  // Fetch a preview when we have a verse but no inline text (edit mode).
  const { data: versePreview } = useVerseText(
    form.previewArabic ? null : form.surahNumber,
    form.previewArabic ? null : form.ayahNumber
  );
  const previewArabic = form.previewArabic || versePreview?.arabic || "";
  const previewTranslation =
    form.previewTranslation || versePreview?.english || "";

  const [createQuote, { loading: creating }] = useMutation(
    CREATE_CMZ_LIBRARY_QUOTE
  );
  const [updateQuote, { loading: updating }] = useMutation(
    UPDATE_CMZ_LIBRARY_QUOTE
  );
  const saving = creating || updating;

  // Hydrate whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setTouched(false);
    setServerError(null);
    setQuery("");
    setResults([]);
    setSearchError(null);
    searchSeq.current++;
    if (quote) {
      setForm({
        reference: quote.reference,
        surahNumber: quote.surahNumber ?? null,
        ayahNumber: quote.ayahNumber ?? null,
        previewArabic: quote.arabicText || "",
        previewTranslation: quote.translation || "",
        tags: quote.tags || [],
        labels: quote.labels || [],
        isVerified: quote.isVerified,
        isActive: quote.isActive,
      });
      setSearchMode(false); // show the existing verse; user can Change it
    } else {
      setForm(emptyForm);
      setSearchMode(true);
    }
  }, [open, quote]);

  // Lazily fetch chapter metadata for building "Surah X 2:201" references.
  useEffect(() => {
    if (!open || Object.keys(chapters).length > 0) return;
    axios
      .get(quranContentUrl("chapters"), { params: { language: "en" } })
      .then((res) => {
        const map: Record<number, ChapterMeta> = {};
        (res.data?.chapters || []).forEach((c: ChapterMeta) => {
          map[c.id] = c;
        });
        setChapters(map);
      })
      .catch(() => {
        /* non-fatal — reference falls back to "Quran 2:201" */
      });
  }, [open, chapters]);

  const set = <K extends keyof FormState>(key: K, val: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  // ── Quran search ────────────────────────────────────────────────────────────
  const runQuranSearch = async () => {
    const q = query.trim();
    if (!q) return;
    const seq = ++searchSeq.current;
    const isStale = () => seq !== searchSeq.current;

    setSearching(true);
    setSearchError(null);
    setResults([]);
    try {
      const directKey = parseVerseKey(q);
      if (directKey) {
        const res = await axios.get(quranContentUrl(`verses/by_key/${directKey}`), {
          params: {
            translations: DEFAULT_TRANSLATION_ID,
            fields: "text_uthmani",
          },
        });
        if (isStale()) return;
        const v = res.data?.verse;
        if (!v) {
          setSearchError(`No verse found for ${directKey}.`);
          return;
        }
        setResults([
          {
            verse_key: v.verse_key,
            text: v.text_uthmani || v.text || "",
            translations: v.translations || [],
          },
        ]);
      } else {
        const res = await axios.get(quranContentUrl("search"), {
          params: { q, size: 10, page: 0, translations: DEFAULT_TRANSLATION_ID },
        });
        if (isStale()) return;
        const hits: QuranSearchResult[] = res.data?.search?.results || [];
        setResults(hits);
        if (hits.length === 0) setSearchError("No verses matched your search.");
      }
    } catch (e: any) {
      if (isStale()) return;
      setSearchError(
        e?.response?.data?.message || e?.message || "Search failed. Try again."
      );
    } finally {
      if (!isStale()) setSearching(false);
    }
  };

  // Pick a verse → store surah/ayah + reference, capture preview, collapse search.
  const applyResult = (r: QuranSearchResult) => {
    const [chStr, ayahStr] = r.verse_key.split(":");
    const surah = Number(chStr);
    const ayah = Number(ayahStr);
    const key = verseKeyOf(surah, ayah);
    const dup = duplicateOf(key);
    if (dup) {
      setSearchError(
        `This verse is already in the library (${dup.reference || key}). Open it from the list to edit.`
      );
      return;
    }
    const ch = chapters[surah];
    const reference = ch
      ? `Surah ${ch.name_simple} ${r.verse_key}`
      : `Quran ${r.verse_key}`;
    setForm((f) => ({
      ...f,
      surahNumber: surah,
      ayahNumber: ayah,
      reference,
      previewArabic: stripHtml(r.text),
      previewTranslation: stripHtml(r.translations?.[0]?.text || ""),
    }));
    setTouched(false);
    setSearchError(null);
    setSearchMode(false);
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!hasVerse || !selectedVerseKey) {
      setSearchMode(true);
      return;
    }
    setServerError(null);

    const dup = duplicateOf(selectedVerseKey);
    if (dup) {
      setServerError(
        `This verse is already in the library (${dup.reference || selectedVerseKey}). You cannot save it twice.`
      );
      return;
    }

    const input: Record<string, any> = {
      category: "QURAN",
      reference: form.reference.trim(),
      surahNumber: form.surahNumber,
      ayahNumber: form.ayahNumber,
      tags: form.tags,
      labels: form.labels,
    };

    try {
      // Refetch every GetCMZLibraryQuotes watcher (paged list + this dialog's
      // duplicate-lookup) so a just-saved verse shows as Saved immediately.
      const refetchOpts = {
        refetchQueries: ["GetCMZLibraryQuotes"],
        awaitRefetchQueries: true,
      };
      if (isEdit && quote) {
        await updateQuote({
          variables: {
            input: {
              ...input,
              id: quote.id,
              isVerified: form.isVerified,
              isActive: form.isActive,
            },
          },
          ...refetchOpts,
        });
      } else {
        await createQuote({ variables: { input }, ...refetchOpts });
      }
      // Dialog may still be mounted briefly; keep lookup warm for reopen.
      void refetchLibraryLookup();
      onClose(true);
    } catch (err: any) {
      setServerError(
        err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          "Something went wrong. Please try again."
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <div className="QV_dialogHeader">
        <Stack direction="row" alignItems="center" gap={1}>
          <MenuBookIcon sx={{ color: "primary.main" }} />
          <Typography variant="h6" fontWeight={700}>
            {isEdit ? "Edit Quran Verse" : "Add Quran Verse"}
          </Typography>
        </Stack>
        <IconButton
          aria-label="close"
          size="small"
          onClick={() => !saving && onClose(false)}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent dividers sx={{ p: 3 }}>
        <Stack gap={2}>
          {serverError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {serverError}
            </Alert>
          )}

          {/* ── Search UI ── */}
          {searchMode && (
            <>
              <Typography variant="body2" color="text.secondary">
                Search by keyword (e.g. <em>patience</em>) or by
                reference (e.g. <strong>62:9</strong>). TVs fetch the text in
                their own language, so only the reference is saved.
              </Typography>
              <Stack direction="row" gap={1}>
                <TextField
                  autoFocus
                  size="small"
                  fullWidth
                  placeholder='Keyword or reference like "2:201"'
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runQuranSearch()}
                />
                <Button
                  variant="contained"
                  onClick={runQuranSearch}
                  disabled={searching || !query.trim()}
                  startIcon={
                    searching ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
                >
                  {searching ? "…" : "Search"}
                </Button>
              </Stack>

              {searchError && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {searchError}
                </Alert>
              )}

              {searching && (
                <Stack gap={1.5}>
                  {[0, 1].map((i) => (
                    <div className="QV_skeletonCard" key={i}>
                      <Skeleton variant="text" width="40%" />
                      <Skeleton variant="text" height={30} />
                      <Skeleton variant="text" width="70%" />
                    </div>
                  ))}
                </Stack>
              )}

              {!searching &&
                results.map((r) => {
                  const [chStr] = r.verse_key.split(":");
                  const ch = chapters[Number(chStr)];
                  const english = stripHtml(r.translations?.[0]?.text || "");
                  const key = r.verse_key;
                  const existing = savedByVerseKey.get(key);
                  const isCurrentEdit =
                    !!quote && !!existing && existing.id === quote.id;
                  const alreadySaved = !!existing && !isCurrentEdit;
                  const isSelected = selectedVerseKey === key || isCurrentEdit;
                  const buttonLocked = alreadySaved || isSelected;

                  return (
                    <Card
                      key={r.verse_key}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      <CardContent sx={{ p: "14px 16px !important" }}>
                        <Stack gap={1}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {ch
                                ? `${ch.name_simple} — ${ch.name_arabic}`
                                : `Surah ${chStr}`}
                            </Typography>
                            <Chip label={r.verse_key} size="small" />
                          </Stack>
                          <div className="QV_arabic" style={{ fontSize: 20 }}>
                            {stripHtml(r.text)}
                          </div>
                          {english && (
                            <div className="QV_english">{english}</div>
                          )}
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => applyResult(r)}
                            disabled={buttonLocked}
                            sx={{ alignSelf: "flex-start", borderRadius: 2 }}
                          >
                            {alreadySaved
                              ? "Saved"
                              : isSelected
                                ? "Selected"
                                : "Use this verse"}
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}

              {isEdit && hasVerse && (
                <Button
                  size="small"
                  onClick={() => setSearchMode(false)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Keep current verse
                </Button>
              )}
            </>
          )}

          {/* ── Selected verse ── */}
          {!searchMode && hasVerse && (
            <div className="QV_row" style={{ boxShadow: "none" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Chip
                  label={form.reference}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: 11 }}
                />
                <Button size="small" onClick={() => setSearchMode(true)}>
                  Change verse
                </Button>
              </Stack>
              <div className="QV_arabic QV_rowArabic">{previewArabic || "…"}</div>
              {previewTranslation && (
                <div className="QV_rowTranslation">{previewTranslation}</div>
              )}
            </div>
          )}

          {/* ── Tags / labels / toggles (shown once a verse exists) ── */}
          {hasVerse && (
            <>
              <ChipInput
                label="Tags"
                value={form.tags}
                onChange={(v) => set("tags", v)}
                placeholder="Type a tag and press Enter"
              />
              <ChipInput
                label="Labels (optional)"
                value={form.labels}
                onChange={(v) => set("labels", v)}
                placeholder="e.g. After-Salah, Morning-Adhkar"
              />

              {isEdit && (
                <>
                  <Divider />
                  <Stack direction="row" gap={3} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.isVerified}
                          onChange={(e) => set("isVerified", e.target.checked)}
                        />
                      }
                      label="Verified"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.isActive}
                          onChange={(e) => set("isActive", e.target.checked)}
                        />
                      }
                      label="Active"
                    />
                  </Stack>
                </>
              )}
            </>
          )}

          <Stack direction="row" gap={1} justifyContent="flex-end" mt={1}>
            <Button onClick={() => onClose(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving || !hasVerse}
              sx={{ borderRadius: 2 }}
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Save verse"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteFormDialog;
